import {
  amber,
  blue,
  blueGrey,
  green,
  grey,
  lightBlue,
  orange,
  red,
} from '@mui/material/colors';
import { createTheme } from '@mui/material/styles';
import '@mui/x-data-grid-premium/themeAugmentation';
import './mui-data-grid-theme-augmentation';
import periversePalette from './periverse-palette';

// Extend TypographyVariants to include custom table typography
declare module '@mui/material/styles' {
  interface TypographyVariants {
    tableHeader: React.CSSProperties;
    tableCell: React.CSSProperties;
    fontWeightSemiBold: React.CSSProperties['fontWeight'];
    body1Strong: React.CSSProperties;
    body2Strong: React.CSSProperties;
    cardTitle: React.CSSProperties;
    buttonLarge: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    body1Strong?: React.CSSProperties;
    body2Strong?: React.CSSProperties;
    cardTitle?: React.CSSProperties;
    buttonLarge?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1Strong: true;
    body2Strong: true;
    cardTitle: true;
    buttonLarge: true;
  }
}

/**
 * PeriVerse Design System Theme
 *
 * This theme is based on the Material-UI design specifications provided in the Figma files.
 * It includes the complete color palette, typography scale, spacing, breakpoints, and component overrides
 * to ensure consistency across all micro-frontend applications.
 */

// MUI grey colors for component tokens - using MUI's built-in colors
const muiGrey = grey;

// Custom Peritas colors only - MUI colors are available through MUI's built-in palette
const customColors = periversePalette;

// Spacing system from Figma
const spacing = {
  0: 4,
  1: 8,
  2: 16,
  3: 24,
  4: 32,
  5: 40,
  6: 48,
  7: 56,
  8: 64,
  9: 72,
  10: 80,
  11: 88,
  12: 96,
};

// Breakpoints aligned with responsive-layout.css requirements
const breakpoints = {
  xs: 320, // Mobile start (320px - 719px)
  sm: 720, // Tablet start (720px - 991px)
  md: 992, // Desktop start (992px - 1599px)
  lg: 1728, // Large desktop start (≥1728px)
  xl: 1920, // Extra large
};

// Shape values from Figma
const shape = {
  borderRadius: {
    none: 0,
    small: 4,
    medium: 8,
    large: 12,
    extraLarge: 24,
  },
};

// Typography from Figma
const typography = {
  fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
  fontWeightLight: 300,
  fontWeightRegular: 400,
  fontWeightMedium: 500,
  fontWeightSemiBold: 600,
  fontWeightBold: 700,
  fontSize: 16, // Base font size in px
  h1: {
    fontSize: '96px',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.01562em',
  },
  h2: {
    fontSize: '60px',
    fontWeight: 300,
    lineHeight: 1.2,
    letterSpacing: '-0.00833em',
  },
  h3: {
    fontSize: '48px',
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: '0em',
  },
  h4: {
    fontSize: '34px',
    fontWeight: 400,
    lineHeight: 1.3,
    letterSpacing: '0.00735em',
  },
  h5: {
    fontSize: '24px',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0em',
  },
  h6: {
    fontSize: '20px',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  subtitle2: {
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: 1.5,
    letterSpacing: '0.00714em',
  },
  body1: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.00938em',
  },
  body1Strong: {
    fontSize: '16px',
    fontWeight: 700,
    lineHeight: '1.5',
    letterSpacing: '0.15px',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontFeatureSettings: '"liga" off, "clig" off',
  },
  body2: {
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.01071em',
  },
  body2Strong: {
    fontSize: '14px',
    fontWeight: 700,
    lineHeight: '143%',
    letterSpacing: '0.17px',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontFeatureSettings: '"liga" off, "clig" off',
  },
  caption: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.03333em',
  },
  overline: {
    fontSize: '12px',
    fontWeight: 400,
    lineHeight: 2.66,
    letterSpacing: '0.08333em',
    textTransform: 'uppercase' as const,
  },
  cardTitle: {
    color: 'rgba(0, 0, 0, 0.87)',
    fontFeatureSettings: '"liga" off, "clig" off',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '133.4%',
  },
  buttonLarge: {
    fontFeatureSettings: '"liga" off, "clig" off',
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontSize: '15px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: 1,
    letterSpacing: '0.46px',
    textTransform: 'capitalize' as const,
  },
  // Table typography from Figma Peritas Material DS
  tableHeader: {
    fontSize: '0.875rem', // 14px
    fontWeight: 500, // fontWeightMedium
    lineHeight: '1.5rem', // 24px
    letterSpacing: '0.0106rem', // 0.17px
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontFeatureSettings: '"liga" off, "clig" off',
  },
  tableCell: {
    fontSize: '0.875rem', // 14px
    fontWeight: 400, // fontWeightRegular
    lineHeight: '1.5rem', // 24px
    letterSpacing: '0.0106rem', // 0.17px
    fontFamily: '"Helvetica Neue", "Roboto", "Arial", sans-serif',
    fontFeatureSettings: '"liga" off, "clig" off',
  },
};

// Create the Material-UI light theme
export const periverseTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: customColors.peritaspurple[500],
      dark: customColors.peritaspurple[600],
      light: customColors.peritaspurple[400],
      contrastText: '#ffffff',
    },
    secondary: {
      main: customColors.peritasgrey[700], // #4e5e64 - Peritas grey main
      dark: customColors.peritasgrey[800], // #404b4f - Peritas grey dark
      light: customColors.peritasgrey[300], // #99a7ad - Peritas grey light
      contrastText: '#ffffff',
    },
    // MUI colors - using MUI's built-in color constants
    error: {
      main: customColors.peritasred[500], // #ff4047
      dark: customColors.peritasred[700],
      light: customColors.peritasred[300],
      contrastText: '#ffffff',
    },
    warning: {
      main: orange[800],
      dark: orange[900],
      light: orange[500],
      contrastText: '#ffffff',
    },
    info: {
      main: blue[500], // #2196f3
      dark: blue[700],
      light: blue[400],
      contrastText: '#ffffff',
    },
    success: {
      main: customColors.peritasgreen,
      dark: green[800],
      light: green[300],
      contrastText: 'rgba(0, 0, 0, 0.87)',
    },
    background: {
      default: customColors.background.default,
      paper: customColors.background.paper,
    },
    text: {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.6)', // Black with 60% opacity
      disabled: 'rgba(0, 0, 0, 0.38)', // Black with 38% opacity
    },
    divider: 'rgba(0, 0, 0, 0.12)',
    // action: {
    //   active: '#000000',
    //   hover: '#000000',
    //   selected: '#000000',
    //   disabled: '#000000',
    //   disabledBackground: '#000000',
    //   focus: '#000000',
    // },
  },
  typography,
  spacing: (factor: number) =>
    spacing[factor as keyof typeof spacing] || factor * 8,
  breakpoints: {
    values: breakpoints,
  },
  shape: {
    borderRadius: shape.borderRadius.medium, // 8px default
  },
  components: {
    // Disable MUI Typography to use our custom typography
    MuiTypography: {
      styleOverrides: {
        root: {
          // Use our custom typography instead of MUI defaults
          fontFamily: typography.fontFamily,
        },
      },
    },
    // MuiDataGrid: {
    //   styleOverrides: {
    //     root: {
    //       backgroundColor: 'red',
    //     },
    //   },
    // },
    MuiButton: {
      styleOverrides: {
        // Fixed button heights per size; lineHeight:1 lets minHeight govern. Helvetica Neue's
        // tall descent makes labels sit ~1px high, so nudge them down with 1px-asymmetric
        // vertical padding. `&&` doubles specificity so this wins over per-button `sx` padding.
        root: { lineHeight: 1 },
        sizeSmall: { minHeight: 30, '&&': { paddingTop: 5, paddingBottom: 3 } },
        sizeMedium: {
          minHeight: 36,
          '&&': { paddingTop: 7, paddingBottom: 5 },
        },
        sizeLarge: { minHeight: 40, '&&': { paddingTop: 9, paddingBottom: 7 } },
      },
    },
    // MuiCard: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: shape.borderRadius.large,
    //     },
    //   },
    // },
    // MuiPaper: {
    //   styleOverrides: {
    //     root: {
    //       borderRadius: shape.borderRadius.medium,
    //     },
    //   },
    // },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.medium,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        columnHeader: {
          backgroundColor: '#ffffff',
          color: 'rgba(0, 0, 0, 0.6)',
          fontFeatureSettings: typography.tableHeader.fontFeatureSettings,
          fontFamily: typography.tableHeader.fontFamily,
          fontSize: typography.tableHeader.fontSize,
          fontWeight: typography.tableHeader.fontWeight,
          lineHeight: typography.tableHeader.lineHeight,
          letterSpacing: typography.tableHeader.letterSpacing,
        },
        columnHeaderTitle: {
          color: 'rgba(0, 0, 0, 0.6)',
          fontFeatureSettings: typography.tableHeader.fontFeatureSettings,
          fontFamily: typography.tableHeader.fontFamily,
          fontSize: typography.tableHeader.fontSize,
          fontWeight: typography.tableHeader.fontWeight,
          lineHeight: typography.tableHeader.lineHeight,
          letterSpacing: typography.tableHeader.letterSpacing,
        },
      },
    },
  },
});

// Create the Material-UI dark theme
const periverseDarkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: customColors.peritaspurple[300],
      dark: customColors.peritaspurple[500],
      light: customColors.peritaspurple[100],
      contrastText: '#000000',
    },
    secondary: {
      main: customColors.peritasgrey[100], // #d6d9d9 - Peritas grey main
      dark: customColors.peritasgrey[200], // #b8c0c3 - Peritas grey dark
      light: customColors.peritasgrey[50], // #efefef - Peritas grey light
      contrastText: '#000000',
    },
    // MUI colors - using MUI's built-in color constants
    error: {
      main: red[500], // #f44336
      dark: red[700], // #d32f2f
      light: red[300], // #e57373
      contrastText: '#ffffff',
    },
    warning: {
      main: orange[400], // #ffa726
      dark: orange[700], // #f57c00
      light: orange[300], // #ffb74d
      contrastText: '#000000',
    },
    info: {
      main: lightBlue[400], // #29b6f6
      dark: lightBlue[700], // #0288d1
      light: lightBlue[300], // #4fc3f7
      contrastText: '#000000',
    },
    success: {
      main: green[400], // #66bb6a
      dark: green[700], // #388e3c
      light: green[300], // #81c784
      contrastText: '#000000',
    },
    background: {
      default: '#646464',
      paper: '#121212',
    },
    text: {
      primary: '#ffffff',
      secondary: '#ffffff',
      disabled: '#ffffff',
    },
    divider: 'rgba(255, 255, 255, 0.12)',
    // action: {\n    //   active: '#ffffff',
    //   hover: '#ffffff',
    //   selected: '#ffffff',
    //   disabled: '#ffffff',
    //   disabledBackground: '#ffffff',
    //   focus: '#ffffff',
    // },
  },
  typography,
  spacing: (factor: number) =>
    spacing[factor as keyof typeof spacing] || factor * 8,
  breakpoints: {
    values: breakpoints,
  },
  shape: {
    borderRadius: shape.borderRadius.medium, // 8px default
  },
  components: {
    // Disable MUI Typography to use our custom typography
    MuiTypography: {
      styleOverrides: {
        root: {
          // Use our custom typography instead of MUI defaults
          fontFamily: typography.fontFamily,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: shape.borderRadius.medium,
        },
      },
    },
  },
});

// Helper function to convert hex color + opacity to rgba
export const applyOpacity = (hexColor: string, opacity: number): string => {
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

// Navy navigation surface (host sidebar + global header). Fixed brand surface,
// independent of light/dark mode. Figma uses grey-50 rather than pure white.
const navigationForeground = '#fafafa';
const onNavy = (opacity: number) => applyOpacity(navigationForeground, opacity);

export const navigationTokens = {
  surface: customColors.peritasnavy,
  title: navigationForeground,
  text: navigationForeground,
  textActive: navigationForeground,
  activeIconBg: customColors.peritaspurple[500],
  hoverBg: onNavy(0.08),
  divider: onNavy(0.12),
  scrollbarThumb: onNavy(0.2),
  brandText: navigationForeground,
  search: {
    surface: onNavy(0.1),
    text: navigationForeground,
    placeholder: onNavy(0.5),
    icon: navigationForeground,
    borderHover: onNavy(0.2),
    borderFocus: onNavy(0.35),
  },
};

// Component-specific design tokens from Figma palette export
export const componentTokens = {
  // Primary states
  primary: {
    dark: customColors.peritaspurple[700],
    baseColor: customColors.peritaspurple[500],
    states: {
      hover: {
        color: customColors.peritaspurple[600],
        opacity: 0.04,
      },
      focus: {
        color: customColors.peritaspurple[500],
        opacity: 0.3,
      },
      focusVisible: {
        color: customColors.peritaspurple[600],
        opacity: 0.3,
      },
      selected: {
        color: customColors.peritaspurple[500],
        opacity: 1.0,
      },
      outlinedBorder: customColors.peritaspurple[500],
    },
  },
  // Secondary states
  secondary: {
    states: {
      hover: customColors.peritasgrey[700],
      selected: customColors.peritasgrey[700],
      focus: customColors.peritasgrey[700],
      focusVisible: customColors.peritasgrey[700],
      outlinedBorder: customColors.peritasgrey[700],
    },
  },
  // Text states
  text: {
    states: {
      hover: '#000000',
      selected: '#000000',
      focus: '#000000',
      focusVisible: '#000000',
    },
  },
  // Error states - reference theme palette
  error: {
    states: {
      hover: red[700],
      selected: red[700],
      focusVisible: red[700],
      outlinedBorder: red[700],
    },
  },
  // Warning states - reference theme palette
  warning: {
    states: {
      hover: orange[800],
      selected: orange[800],
      focusVisible: orange[800],
      outlinedBorder: orange[800],
    },
  },
  // Info states - reference theme palette
  info: {
    states: {
      hover: lightBlue[700],
      selected: lightBlue[700],
      focusVisible: lightBlue[700],
      outlinedBorder: lightBlue[700],
      buttonOutline: { color: customColors.peritasblue[700], opacity: 0.5 },
    },
  },
  // Success states - reference theme palette
  success: {
    states: {
      hover: green[800],
      selected: green[800],
      focusVisible: green[800],
      outlinedBorder: green[800],
    },
  },
  // Common states
  common: {
    white: {
      states: {
        main: '#ffffff',
        hover: '#ffffff',
        selected: '#ffffff',
        focus: '#ffffff',
        focusVisible: '#ffffff',
        outlinedBorder: '#ffffff',
      },
    },
    black: {
      states: {
        main: '#000000',
        hover: '#000000',
        selected: '#000000',
        focus: '#000000',
        focusVisible: '#000000',
        outlinedBorder: '#000000',
      },
    },
  },
  // Background elevations
  background: {
    paper: {
      elevation: {
        0: '#ffffff',
        1: '#ffffff',
        2: '#ffffff',
        3: '#ffffff',
        4: '#ffffff',
        5: '#ffffff',
        6: '#ffffff',
        7: '#ffffff',
        8: '#ffffff',
        9: '#ffffff',
        10: '#ffffff',
        11: '#ffffff',
        12: '#ffffff',
        13: '#ffffff',
        14: '#ffffff',
        15: '#ffffff',
        16: '#ffffff',
        17: '#ffffff',
        18: '#ffffff',
        19: '#ffffff',
        20: '#ffffff',
        21: '#ffffff',
        22: '#ffffff',
        23: '#ffffff',
        24: '#ffffff',
      },
    },
  },
  // Component-specific tokens
  components: {
    // Case Schedule page colors
    caseSchedule: {
      // Non-priority case item colors
      caseItem: {
        hoverBackground: '#e6e6e6',
        hoverBorder: '#cacaca',
      },
      // Priority case item colors
      priorityCaseItem: {
        border: '#f44336',
        background: '#feebee',
        hoverBackground: '#fecdd2',
        activeBackground: '#ffffff',
      },
      // CTA button colors
      ctaButton: {
        // Non-priority
        hoverDefault: customColors.peritaspurple[600],
        activeDefault: customColors.peritaspurple[300],
        activeTextDefault: '#ffffff',
        // Priority
        defaultPriority: '#ff4047',
        hoverPriority: '#c62828',
        activePriority: '#e06d6d',
      },
    },
    // Cart Status Chip colors based on Figma design
    cartStatus: {
      dispatched: {
        background: green[50],
        text: green[800],
        border: green[50],
      },
      dispatchedIncomplete: {
        background: green[50],
        text: green[800],
        border: green[600],
      },
      readyForDispatch: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
      traysUnavailable: {
        background: amber[50],
        text: '#E86500',
        border: orange[600],
      },
      inProgress: {
        background: blueGrey[50],
        text: blueGrey[800],
        border: blueGrey[300],
      },
      incomplete: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
      notStarted: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
    },
    // Picking Station Status Chip colors based on Figma design
    pickingStationStatus: {
      available: {
        background: green[50],
        text: green[800],
        border: green[50],
      },
      busy: {
        background: '#fff4e5',
        text: '#663C00',
        border: '#fff4e5',
      },
      offline: {
        background: '#fff4e5',
        text: '#663C00',
        border: '#fff4e5',
      },
    },
    rating: {
      enabledBorder: '#000000',
      activeFill: '#ffb400',
    },
    avatar: {
      fill: muiGrey[400],
    },
    input: {
      standard: {
        enabledBorder: '#000000',
        hoverBorder: '#000000',
      },
      filled: {
        enabledFill: '#000000',
        hoverFill: '#000000',
      },
      outlined: {
        enabledBorder: '#000000',
        hoverBorder: '#000000',
      },
    },
    switch: {
      knobFillEnabled: muiGrey[50],
      slideFill: '#000000',
      knobFillDisabled: muiGrey[100],
    },
    elevation: {
      outlined: muiGrey[300],
    },
    chip: {
      default: {
        closeFill: '#000000',
        hoverFill: '#000000',
        enabledBorder: muiGrey[400],
        focusFill: '#000000',
      },
    },
    tooltip: {
      fill: muiGrey[700],
    },
    backdrop: {
      fill: '#000000',
    },
    appbar: {
      defaultFill: muiGrey[100],
    },
    breadcrumbs: {
      collapseFill: muiGrey[100],
    },
    nativeScrollbar: {
      bg: muiGrey[200],
    },
    alert: {
      error: {
        color: '#5f2120',
        background: '#fdeded',
      },
      warning: {
        color: '#663c00',
        background: '#fff4e5',
      },
      info: {
        color: '#014361',
        background: '#e5f6fd',
      },
      success: {
        color: '#1e4620',
        background: '#edf7ed',
      },
    },
    stepper: {
      connector: muiGrey[400],
    },
    snackbar: {
      fill: muiGrey[800],
    },
  },
};

// Dark mode component-specific design tokens from Figma palette export
export const darkComponentTokens = {
  // Primary states
  primary: {
    baseColor: customColors.peritaspurple[300], // #8b91ea
    states: {
      hover: {
        color: customColors.peritaspurple[300], // #8b91ea
        opacity: 0.04, // 4%
      },
      focus: {
        color: customColors.peritaspurple[300], // #8b91ea
        opacity: 0.3, // 30%
      },
      focusVisible: {
        color: customColors.peritaspurple[300], // #8b91ea
        opacity: 0.3, // 30%
      },
      selected: {
        color: customColors.peritaspurple[300], // #8b91ea
        opacity: 1.0, // 100%
      },
      outlinedBorder: customColors.peritaspurple[300], // solid color, no opacity needed
    },
  },
  // Secondary states
  secondary: {
    states: {
      hover: customColors.peritasgrey[100],
      selected: customColors.peritasgrey[100],
      focus: customColors.peritasgrey[100],
      focusVisible: customColors.peritasgrey[100],
      outlinedBorder: customColors.peritasgrey[100],
    },
  },
  // Text states
  text: {
    states: {
      hover: '#ffffff',
      selected: '#ffffff',
      focus: '#ffffff',
      focusVisible: '#ffffff',
    },
  },
  // Error states
  error: {
    states: {
      hover: red[500],
      selected: red[500],
      focusVisible: red[500],
      outlinedBorder: red[500],
    },
  },
  // Warning states
  warning: {
    states: {
      hover: orange[400],
      selected: orange[400],
      focusVisible: orange[400],
      outlinedBorder: orange[400],
    },
  },
  // Info states
  info: {
    states: {
      hover: lightBlue[400],
      selected: lightBlue[400],
      focusVisible: lightBlue[400],
      outlinedBorder: lightBlue[400],
    },
  },
  // Success states
  success: {
    states: {
      hover: green[400],
      selected: green[400],
      focusVisible: green[400],
      outlinedBorder: green[400],
    },
  },
  // Common states
  common: {
    white: {
      states: {
        main: '#ffffff',
        hover: '#ffffff',
        selected: '#ffffff',
        focus: '#ffffff',
        focusVisible: '#ffffff',
        outlinedBorder: '#ffffff',
      },
    },
    black: {
      states: {
        main: '#000000',
        hover: '#000000',
        selected: '#000000',
        focus: '#000000',
        focusVisible: '#000000',
        outlinedBorder: '#000000',
      },
    },
  },
  // Background elevations for dark mode
  background: {
    paper: {
      elevation: {
        0: '#121212',
        1: '#1e1e1e',
        2: '#232323',
        3: '#252525',
        4: '#272727',
        5: '#2a2a2a',
        6: '#2c2c2c',
        7: '#2c2c2c',
        8: '#2e2e2e',
        9: '#2e2e2e',
        10: '#313131',
        11: '#313131',
        12: '#333333',
        13: '#333333',
        14: '#333333',
        15: '#333333',
        16: '#363636',
        17: '#363636',
        18: '#363636',
        19: '#363636',
        20: '#383838',
        21: '#383838',
        22: '#383838',
        23: '#383838',
        24: '#383838',
      },
    },
  },
  // Component-specific tokens for dark mode
  components: {
    // Case Schedule page colors (same as light mode for now)
    caseSchedule: {
      // Non-priority case item colors
      caseItem: {
        hoverBackground: '#e6e6e6',
        hoverBorder: '#cacaca',
      },
      // Priority case item colors
      priorityCaseItem: {
        border: '#f44336',
        background: '#feebee',
        hoverBackground: '#fecdd2',
        activeBackground: '#ffffff',
      },
      // CTA button colors
      ctaButton: {
        // Non-priority
        hoverDefault: customColors.peritaspurple[600],
        activeDefault: customColors.peritaspurple[300],
        activeTextDefault: '#ffffff',
        // Priority
        defaultPriority: '#ff4047',
        hoverPriority: '#c62828',
        activePriority: '#e06d6d',
      },
    },
    // Cart Status Chip colors based on Figma design (same as light mode)
    cartStatus: {
      dispatched: {
        background: green[50],
        text: green[800],
        border: green[50],
      },
      dispatchedIncomplete: {
        background: green[50],
        text: green[800],
        border: green[600],
      },
      readyForDispatch: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
      traysUnavailable: {
        background: amber[50],
        text: '#E86500',
        border: orange[600],
      },
      inProgress: {
        background: blueGrey[50],
        text: blueGrey[800],
        border: blueGrey[300],
      },
      incomplete: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
      notStarted: {
        background: blue[50],
        text: blue[800],
        border: blue[400],
      },
    },
    rating: {
      enabledBorder: '#ffffff',
      activeFill: '#ffb400',
    },
    avatar: {
      fill: muiGrey[600],
    },
    input: {
      standard: {
        enabledBorder: '#ffffff',
        hoverBorder: '#ffffff',
      },
      filled: {
        enabledFill: '#ffffff',
        hoverFill: '#ffffff',
      },
      outlined: {
        enabledBorder: '#ffffff',
        hoverBorder: '#ffffff',
      },
    },
    switch: {
      knobFillEnabled: muiGrey[300],
      slideFill: '#ffffff',
      knobFillDisabled: muiGrey[600],
    },
    elevation: {
      outlined: '#ffffff', // divider color
    },
    chip: {
      default: {
        closeFill: '#ffffff',
        hoverFill: '#ffffff',
        enabledBorder: muiGrey[700],
        focusFill: '#ffffff',
      },
    },
    tooltip: {
      fill: muiGrey[700],
    },
    backdrop: {
      fill: '#000000',
    },
    appbar: {
      defaultFill: '#272727', // background-paper-elevation-4
    },
    breadcrumbs: {
      collapseFill: muiGrey[600],
    },
    nativeScrollbar: {
      bg: muiGrey[700],
    },
    alert: {
      error: {
        color: '#f4c7c7',
        background: '#160b0b',
      },
      warning: {
        color: '#ffe2b7',
        background: '#191207',
      },
      info: {
        color: '#b8e7fb',
        background: '#071318',
      },
      success: {
        color: '#cce8cd',
        background: '#0c130d',
      },
    },
    stepper: {
      connector: muiGrey[600],
    },
    snackbar: {
      fill: '#2c2c2c', // background-paper-elevation-6
    },
  },
};

// Helper function to get cart status colors for light mode
export const getCartStatusColors = (status: string) => {
  switch (status) {
    case 'Dispatched':
      return componentTokens.components.cartStatus.dispatched;
    case 'Dispatched - Incomplete':
      return componentTokens.components.cartStatus.dispatchedIncomplete;
    case 'Ready for Dispatch':
      return componentTokens.components.cartStatus.readyForDispatch;
    case 'Trays Unavailable':
      return componentTokens.components.cartStatus.traysUnavailable;
    case 'In Progress':
      return componentTokens.components.cartStatus.inProgress;
    case 'Incomplete':
      return componentTokens.components.cartStatus.incomplete;
    case 'Not Started':
      return componentTokens.components.cartStatus.notStarted;
    default:
      return componentTokens.components.cartStatus.notStarted; // fallback
  }
};

// Helper function to get cart status colors for dark mode
export const getDarkCartStatusColors = (status: string) => {
  switch (status) {
    case 'Dispatched':
      return darkComponentTokens.components.cartStatus.dispatched;
    case 'Dispatched - Incomplete':
      return darkComponentTokens.components.cartStatus.dispatchedIncomplete;
    case 'Ready for Dispatch':
      return darkComponentTokens.components.cartStatus.readyForDispatch;
    case 'Trays Unavailable':
      return darkComponentTokens.components.cartStatus.traysUnavailable;
    case 'In Progress':
      return darkComponentTokens.components.cartStatus.inProgress;
    case 'Incomplete':
      return darkComponentTokens.components.cartStatus.incomplete;
    case 'Not Started':
      return darkComponentTokens.components.cartStatus.notStarted;
    default:
      return darkComponentTokens.components.cartStatus.notStarted; // fallback
  }
};

// Helper function to get picking station status colors
export const getPickingStationStatusColors = (status: string) => {
  switch (status) {
    case 'Available':
      return componentTokens.components.pickingStationStatus.available;
    case 'Busy':
      return componentTokens.components.pickingStationStatus.busy;
    case 'Offline':
      return componentTokens.components.pickingStationStatus.offline;
    default:
      return componentTokens.components.pickingStationStatus.offline; // fallback
  }
};

// Export custom color values for direct use
export const themeColors = customColors;

// Export typography values for direct use
export const themeTypography = typography;

// Export spacing values for direct use
export const themeSpacing = spacing;

// Export breakpoint values for direct use
export const themeBreakpoints = breakpoints;

// Export shape values for direct use
export const themeShape = shape;

// Export both themes
export { periverseTheme as default, periverseDarkTheme };
